<template>
    <div>
        <FullPageLoader :isLoading="saving"/>

        <ContractsRecordCreateModal
            :show="show_create_modal"
            :status="create_status"
            :customerEmail="_.has(record.step2, 'customer') ? record.step2.customer.email : ''"
            @create="create"
            @cancelCreatingContract="cancelCreatingContract"
        />

        <!-- Weekly calculation mode modal -->
        <b-modal :active.sync="show_calc_mode_modal" :width="640" scroll="keep">
            <div class="modal-card">
                <header class="modal-card-head"><p class="modal-card-title">Prijsberekening bij wekelijkse facturatie</p></header>
                <section class="modal-card-body" style="border-radius: 0;">
                    <p>Kies hoe de prijs berekend moet worden voor wekelijkse facturen:</p>

                    <h5>Dagprijs</h5>
                    <p class="marginbottom0">
                        De prijs wordt omgerekend naar een dagtarief en vervolgens vermenigvuldigd met het aantal dagen in de factuurperiode.
                        <b-message type="is-info" class="margintop10">
                            <strong>Voorbeeld:</strong> Product €100 → €100 / {{ simpleDenom }} dagen = €{{ simplePerDay }} per dag.<br>
                            Voor een periode van {{ weeklyWeeks }} {{ weeklyWeeks === 1 ? 'week' : 'weken' }} ({{ weeklyWeeks * 7 }} dagen) = €{{ simpleTotal }}
                        </b-message>
                    </p>
                    <hr>
                    <h5>Productprijs</h5>
                    <p class="marginbottom0">
                        De opgegeven productprijs wordt gezien als de vaste prijs, ongeacht het aantal dagen.
                        <b-message type="is-info" class="margintop10">
                            <strong>Voorbeeld:</strong> Product €100 → €100 per {{ weeklyWeeks }} {{ weeklyWeeks === 1 ? 'week' : 'weken' }}.
                        </b-message>
                    </p>
                </section>
                <footer class="modal-card-foot">
                    <b-button type="is-light" @click="show_calc_mode_modal = false">Annuleren</b-button>
                    <b-button type="is-primary" @click="applyCalcMode('per_day')">Dagprijs</b-button>
                    <b-button type="is-dark" @click="applyCalcMode('fixed_price')">Productprijs</b-button>
                </footer>
            </div>
        </b-modal>

        <!-- First contract for this location info modal -->
        <b-modal :active.sync="show_first_location_modal" :width="640" scroll="keep">
            <div class="modal-card">
                <header class="modal-card-head"><p class="modal-card-title">Eerste contract voor deze {{ has_multiple_locations ? 'vestiging' : 'organisatie' }}</p></header>
                <section class="modal-card-body" style="border-radius: 0;">
                    <p>
                        Dit is het eerste contract voor deze {{ has_multiple_locations ? 'vestiging' : 'organisatie' }}.
                        Controleer eerst of alle instellingen en sjablonen correct zijn ingesteld.
                    </p>
                </section>
                <footer class="modal-card-foot">
                    <b-button type="is-primary" @click="show_first_location_modal = false">Begrepen</b-button>
                </footer>
            </div>
        </b-modal>

        <b-notification class="row" v-if="$store.state.env === 'local' || $store.state.env === 'test'">
            <div class="column text-center">
                <h3>Test omgeving debug - Klant type: {{ record?.step2?.customer?.type || 'geen klant gekoppeld' }}</h3>
                <button class="button is-primary" @click="activeStep = 0">Eerste stap</button>
                <!-- <button class="button is-dark" @click="record['step2']['customer']['type'] = record['step2']['customer']['type'] == 'business' ? 'normal' : 'business'">Toggle normale/zakelijke klant</button> -->
                <button class="button is-warning" @click="activeStep = 2">Producten</button>
                <button class="button is-success" @click="activeStep = 3">Laatste stap</button>
            </div>
        </b-notification>

        <div class="row">
            <div class="column medium-4 text-right float-right">
                <b-tooltip :label="hideSensitiveData ? 'Toon gevoelige data' : 'Verberg gevoelige data'" position="is-left" v-if="mode == 'new'">
                    <b-button :type="hideSensitiveData ? 'is-success' : 'is-danger'" :disabled="!_.isNull(record.step2.customer_id) && activeStep == 1" :icon-right="hideSensitiveData ? 'eye' : 'eye-slash'" @click="$store.dispatch('toggleSensitiveData')"></b-button>
                </b-tooltip>
                <b-button tag="router-link" to="/panel/contracten/" type="is-dark">Terug naar overzicht</b-button>
            </div>
            <div class="column medium-8">
                <h1>Contract {{ mode == 'new' ? 'aanmaken' : 'bekijken' }}</h1>
            </div>
        </div>

        <div class="row">
            <div class="column">
                <form class="form">
                    <b-steps v-model="activeStep" :animated="true" :has-navigation="false" class="margintop10">
                        <b-step-item label="Algemeen" :clickable="!!validatedSteps.step1" :type="{ 'is-success': !!validatedSteps.step0 }">
                            <div class="row">
                                <div class="column small-12 medium-6 paddingtop0">
                                    <b-field
                                        label="Vestiging"
                                        v-if="user.organisation_locations && user.organisation_locations.length > 1"
                                        :message="getError(errors[1], 'location')"
                                        :type="getType(errors[1], 'location', '')"
                                    >
                                        <b-select placeholder="Selecteer een locatie" v-model="record.step1.location" expanded>
                                            <option
                                                v-for="location in user.organisation_locations"
                                                :value="location.id"
                                                :key="'location_'+location.id"
                                                :selected="location.id == record.step1.location"
                                            >
                                                {{ location.name }} {{ location.city ? `- ${location.city}` : '' }}
                                            </option>
                                        </b-select>
                                    </b-field>
                                </div>
                            </div>

                            <div class="row">
                                <div class="column small-12 medium-6 large-4 paddingtop0">
                                    <b-field label="Startdatum"
                                        :message="getError(errors[1], 'start_date')"
                                        :type="getType(errors[1], 'start_date', '')"
                                    >
                                        <b-datepicker
                                            placeholder="Klik om te selecteren"
                                            :nearby-selectable-month-days="true"
                                            icon="calendar-alt"
                                            icon-pack="fas"
                                            :min-date="moment(new Date()).startOf('day').toDate()"
                                            v-model="record.step1.start_date"
                                        />
                                    </b-field>

                                    <b-checkbox v-model="undetermined" class="marginbottom15">
                                        Contract is voor onbepaalde tijd
                                    </b-checkbox>
                                </div>
                            </div>

                            <ContractBillingSettingsFields
                                v-model="record.step1"
                                :paymentTypes="payment_types"
                                :disableIncasso="!user.organisation_incasso"
                                :invoiceFreqMessage="getError(errors[1], 'invoice_freq')"
                                :invoiceFreqType="getType(errors[1], 'invoice_freq', '')"
                                :paymentTypeMessage="getError(errors[1], 'payment_type')"
                                :paymentTypeType="getType(errors[1], 'payment_type', '')"
                            />

                            <slide-up-down :active="!undetermined" :duration="500">
                                <div class="row">
                                    <div class="column paddingbottom0 small-12 medium-6 large-4">
                                        <b-field label="Aantal facturen"></b-field>
                                        <b-field
                                            :message="getError(errors[1], 'length')"
                                            :type="getType(errors[1], 'length', '')"
                                        >
                                            <b-select placeholder="Maak een keuze" v-model="record.step1.length" v-if="mode == 'new'" expanded>
                                                <option
                                                    v-for="n in 59"
                                                    :value="n + 1"
                                                    :key="'contractdag_'+(n + 1)"
                                                >{{ n + 1 }}</option>
                                            </b-select>
                                            <div v-else>
                                                {{ moment(record.step1.end_date).format('DD-MM-YYYY') }}
                                            </div>
                                        </b-field>
                                    </div>
                                </div>

                                <div v-if="record.step1.length && record.step1.invoice_freq && mode == 'new'">
                                    <b-message type="is-info" class="margintop10">
                                        <p><strong>Het contract loopt af op:</strong>&nbsp;{{ moment(contractExpirationDate).format('D-M-Y') }}</p>
                                    </b-message>
                                    <br>
                                </div>
                            </slide-up-down>

                        </b-step-item>

                        <b-step-item label="Klant" :clickable="!!validatedSteps.step2" :type="{ 'is-success': !!validatedSteps.step1 }" ref="customer_tab">
                            <br>
                            <div class="column small-7" style="padding-left:0;">
                                <h1 class="title">Klant {{ record.step2.customer_id ? 'gekoppeld' : 'aanmaken' }}</h1>
                            </div>

                            <div v-show="record.step2.customer_id">
                                <CRUDCustomer
                                    ref="CRUDcustomer"
                                    :id="record.step2.customer_id"
                                    :getCustomer="record.step2.customer"
                                    :showAlert="true"
                                    :canRemoveCustomer="canRemoveCustomer"
                                    :organisation="record.step1.organisation"
                                    :ibanRequired="ibanRequired"
                                    embedded_from="ContractsRecord"
                                    @removeCustomer="removeCustomer"
                                    @fullPageLoader="fullPageLoader"
                                    @customerModeChanged="customerModeChanged"
                                    @linkCustomer="linkCustomer"
                                />
                            </div>
                            <div v-show="!record.step2.customer_id">
                                <FindCustomer
                                    ref="findCustomer"
                                    :organisation="record.step1.organisation"
                                    :showAllCustomers="record.step2.customer_id ? false : true"
                                    :ibanRequired="ibanRequired"
                                    @linkCustomer="linkCustomer"
                                />
                            </div>
                        </b-step-item>

                        <b-step-item label="Producten" :clickable="!!validatedSteps.step3" :type="{ 'is-success': !!validatedSteps.step2 }">
                            <h1 class="title">Producten</h1>

                            <b-notification v-if="record.step2.customer && record.step2.customer.type == 'business'" type="is-info" aria-close-label="Sluiten">
                                <strong>Let op:</strong> getoonde prijzen zijn exclusief BTW (zakelijke klant)
                            </b-notification>

                            <b-notification v-if="record.step2.customer && record.step2.customer == 'foundation'" type="is-info" aria-close-label="Sluiten">
                                <strong>Let op:</strong> klant betreft een stichting, BTW wordt niet getoond op de factuur.
                            </b-notification>

                            <h3>Box(en)</h3>
                            <ContractsRecordProducts
                                ref="ContractsRecordProducts_boxes"
                                type="boxes"
                                :mode="mode"
                                :record="record"
                                :startDate="record.step1.start_date"
                                :organisation="record.step1.organisation"
                                @nextStepBlockedByInvalidBox="setNextStepBlockedByInvalidBox"
                            />

                            <hr>

                            <h3>Product(en)</h3>
                            <ContractsRecordProducts
                                ref="ContractsRecordProducts_products"
                                type="products"
                                :mode="mode"
                                :record="record"
                                :startDate="record.step1.start_date"
                                :organisation="record.step1.organisation"
                            />
                        </b-step-item>

                        <b-step-item label="Overzicht" v-if="mode == 'new'" :clickable="!!validatedSteps.step4" :type="{ 'is-success': !!validatedSteps.step3 }">
                            <h1 class="title">Afronden</h1>
                            <h3>Algemeen</h3>

                            <div :key="overviewKey">
                                <b-field label="Startdatum">
                                    <span class="underlined">{{ moment(record.step1.start_date).format('D-M-Y') }}</span>
                                </b-field>
                                <b-field label="Einddatum" v-if="undetermined">
                                    <span class="underlined">Contract is voor onbepaalde tijd</span>
                                </b-field>
                                <slide-up-down :active="!undetermined" :duration="500">
                                    <b-field label="Aantal facturen">
                                        <span class="underlined">{{ record.step1.length }}x {{ record.step1.invoice_freq == 'month' ? 'maandelijkse factuur' :
                                            record.step1.invoice_freq == 'week_1' ? 'wekelijkse factuur' :
                                            record.step1.invoice_freq == 'week_2' ? 'tweewekelijkse factuur' :
                                            record.step1.invoice_freq == 'week_3' ? 'driewekelijkse factuur' :
                                            record.step1.invoice_freq == 'week_4' ? 'vierwekelijkse factuur' :
                                            record.step1.invoice_freq == 'week_5' ? 'vijfwekelijkse factuur' :
                                            record.step1.invoice_freq == 'quarter' ? 'kwartaal factuur' :
                                            record.step1.invoice_freq == 'halfyear' ? 'halfjaarlijkse factuur' :
                                            record.step1.invoice_freq == 'year' ? 'jaarlijkse factuur' : 'Onbekend' }}</span>
                                    </b-field>
                                    <b-field label="Verloopdatum">
                                        <span class="underlined">{{ moment(contractExpirationDate).format('D-M-Y') }}</span>
                                    </b-field>
                                </slide-up-down>
                                <b-field label="Facturatie frequentie">
                                    <span class="underlined">{{ record.step1.invoice_freq == 'month' ? 'Maandelijks' :
                                                        record.step1.invoice_freq == 'week_1' ? 'Wekelijks' :
                                                        record.step1.invoice_freq == 'week_2' ? 'Elke 2 weken' :
                                                        record.step1.invoice_freq == 'week_3' ? 'Elke 3 weken' :
                                                        record.step1.invoice_freq == 'week_4' ? 'Elke 4 weken' :
                                                        record.step1.invoice_freq == 'week_5' ? 'Elke 5 weken' :
                                                        record.step1.invoice_freq == 'quarter' ? '1x per kwartaal' :
                                                        record.step1.invoice_freq == 'halfyear' ? '1x per halfjaar' :
                                                        record.step1.invoice_freq == 'year' ? '1x per jaar' : 'Onbekend' }}</span>
                                </b-field>
                                <b-field label="Betaalmethode">
                                    <span class="underlined">{{ record.step1.payment_type == 'incasso' ? 'Automatische incasso' : record.step1.payment_type == 'manual' ? 'Handmatig' : 'Onbekend' }}</span>
                                </b-field>
                            </div>

                            <br>
                            <br>

                            <div v-if="_.has(record.step2, 'customer') && record.step2.customer.type">
                                <h3>Klant</h3>
                                <b-button
                                    type="is-light"
                                    size="is-small"
                                    @click="showCustomerInfo = !showCustomerInfo"
                                >
                                    {{ showCustomerInfo ? 'Details verbergen' : 'Details tonen' }}
                                </b-button>
                                <slide-up-down :active="showCustomerInfo" :duration="500">
                                <CRUDCustomer
                                    v-if="showCustomerInfo"
                                    :id="record.step2.customer_id"
                                    :getCustomer="record.step2.customer"
                                    :canRemoveCustomer="false"
                                    :showAlert="true"
                                    :hideEditButton="true"
                                />
                                </slide-up-down>

                                <br>
                                <br>

                                <h2>Facturatie</h2>

                                <div class="row" v-if="typeof record.step1.invoice_freq === 'string' && record.step1.invoice_freq.indexOf('week_') === 0">
                                    <div class="column">
                                        <b-field>
                                            <label class="label">
                                                Prijsberekening&nbsp;-&nbsp;<span class="cursor-pointer"><a @click.prevent="show_calc_mode_modal = true"><u>Voorbeeld</u></a></span>
                                            </label>
                                        </b-field>
                                        <b-field>
                                            <b-radio-button v-model="record.step1.price_calc_mode" native-value="per_day" type="is-primary" :disabled="false">
                                                Dagprijs
                                            </b-radio-button>
                                            <b-radio-button v-model="record.step1.price_calc_mode" native-value="fixed_price" type="is-dark" :disabled="false">
                                                Productprijs
                                            </b-radio-button>
                                        </b-field>
                                        <br><br>
                                    </div>
                                </div>
                                <ContractsRecordInvoiceProducts
                                    v-if="_.has(record.step2, 'customer') && record.step2.customer.type"
                                    ref="ContractsRecordInvoiceProducts"
                                    :record="record"
                                    :contractExpirationDate="contractExpirationDate"
                                    :customerType="record.step2.customer.type"
                                    :products="step3Data"
                                    :startDate="_.has(record, 'step1.start_date') ? new Date(record.step1.start_date) : null"
                                    :invoiceType="invoiceType"
                                    :showBTWforCustomers="showBTWforCustomers"
                                    :invoice_freq="record.step1.invoice_freq"
                                    :price_calc_mode="record.step1.price_calc_mode"
                                    :key="'invoice-products-' + overviewKey"
                                />
                            </div>
                            <div v-else>
                                <b-notification type="is-danger" aria-close-label="Sluiten">
                                    <strong>Let op:</strong> er is geen klant geselecteerd!
                                </b-notification>
                            </div>
                        </b-step-item>
                    </b-steps>
                </form>
            </div>
        </div>

        <slide-up-down :active="showFormFooter" :duration="500">
            <div class="row">
                <div class="column text-right">
                    <div class="formfooter">
                        <b-button type="is-light" v-if="activeStep > 0" @click="prevStep()">Vorige stap</b-button>

                        <b-button
                            type="is-success"
                            :loading="saving === true"
                            @click="activeStep < 3 ? nextStep() : showCreateModal()"
                            :disabled="activeStep == 2 && nextStepBlockedByInvalidBox || activeStep == 3 && !record.step2.customer_id"
                            v-text="activeStep < 3 ? 'Volgende stap' : 'Opslaan en versturen'"
                        ></b-button>
                    </div>
                </div>
            </div>
        </slide-up-down>
    </div>
</template>

<script>
import Vue from 'vue'
import qs from 'querystringify'
import CRUDCustomer from '@/vue/CRUDCustomer'
import FindCustomer from '@/vue/FindCustomer'
import ContractsRecordProducts from './ContractsRecordProducts'
import ContractsRecordInvoiceProducts from './ContractsRecordInvoiceProducts'
import ContractsRecordCreateModal from './ContractsRecordCreateModal'
import ContractBillingSettingsFields from './components/ContractBillingSettingsFields'
// import ContractsRecordEndContractModal from './ContractsRecordEndContractModal'
// import ContractsRecordConfirmStopModal from './ContractsRecordConfirmStopModal'
import formvalidationMixin from '@/mixins/formvalidationMixin.js'
import invoiceFreqMixin from '@/mixins/invoiceFreqMixin'

export default {
    name: 'ContractsRecord',

    components: {
        CRUDCustomer,
        FindCustomer,
        ContractsRecordProducts,
        ContractsRecordInvoiceProducts,
        ContractsRecordCreateModal,
        ContractBillingSettingsFields,
        // ContractsRecordConfirmStopModal,
        // ContractsRecordEndContractModal
    },

    mixins: [formvalidationMixin, invoiceFreqMixin],

    data() {
        return {
            overviewKey: 0,
            mode: 'new',
            saving: false,
            activeStep: 0,
            showFormFooter: true,
            validatedSteps: {
                step0: false,
                step1: false,
                step2: false,
                step3: false,
                step4: false
            },
            canRemoveCustomer: false,
            contractExpirationDate: null,
            secondInvoiceDate: null,
            undetermined: true,
            payment_types: {
                incasso: {
                    label: 'Automatische incasso',
                    iban: true
                },
                manual: {
                    label: 'Handmatig',
                    iban: false
                }
            },
            record: {
                step1: {
                    start_date: moment().startOf('day')._d,
                    length: 0,
                    payment_type: null,
                    organisation: null,
                    invoice_freq: 'month'
                },
                step2: {
                    customer_id: null
                },
                step3: {
                    boxes: [],
                    products: []
                }
            },
            errors: {},
            show_create_modal: false,
            create_status: '',
            finalized: false,
            nextStepBlockedByInvalidBox: false,
            showCustomerInfo: false,
            show_calc_mode_modal: false,
            show_first_location_modal: false,
            has_multiple_locations: false
        }
    },

    computed: {
        user() {
            return this.$store.state.user
        },

        invoiceType() {
            return moment(new Date(this.record.step1.start_date)).format('D') < 15 ? 'pre15' : 'after15'
        },

        organisations() {
            return this.$store.state.organisations ? this.$store.state.organisations : null
        },

        hideSensitiveData() {
            return this.$store.state.hideSensitiveData
        },

        hideEndingContract() {
            let hideEndingContract = false

            if(_.has(this.user.organisation_settings, 'strictcontracts') && this.user.organisation_settings.strictcontracts == 1 && (this.user.role != 'pit' && !this.user.is_admin)){
                hideEndingContract = true
            }

            return hideEndingContract
        },

        showBTWforCustomers() {
            let showBTWforCustomers = true

            if(_.has(this.user.organisation_settings, 'hide_tax_invoice') && this.user.organisation_settings.hide_tax_invoice == 1 && (_.has(this.record, 'step2.customer') && this.record.step2.customer.type == 'normal')){
                showBTWforCustomers = false
            }

            return showBTWforCustomers
        },

        ibanRequired() {
            if (!this.record?.step1?.payment_type) {
                return false
            }

            const paymentType = this.payment_types[this.record.step1.payment_type]
            return paymentType?.iban ?? false
        },

        step3Data() {
            return {
                boxes: this.$store.getters['contract/getProducts']('boxes'),
                products: this.$store.getters['contract/getProducts']('products')
            }
        },

        weeklyWeeks(){
            if(typeof this.record.step1.invoice_freq === 'string' && this.record.step1.invoice_freq.indexOf('week_') === 0){
                const parts = this.record.step1.invoice_freq.split('_')
                const w = parts.length > 1 ? parseInt(parts[1]) : 1
                return isNaN(w) ? 1 : w
            }
            return 0
        },

        simpleDenom(){
            try {
                return moment(this.record.step1.start_date).daysInMonth()
            } catch (e) { return moment().daysInMonth() }
        },

        simplePerDay(){
            // Example always uses 100 euros
            return (100 / this.simpleDenom).toFixed(2)
        },

        simpleTotal(){
            return (parseFloat(this.simplePerDay) * (this.weeklyWeeks * 7)).toFixed(2)
        }
    },

    created(){
        this.startUp()
    },

    beforeDestroy() {
        // Clear all products and boxes when leaving the component
        this.$store.dispatch('contract/clearProducts', 'boxes')
        this.$store.dispatch('contract/clearProducts', 'products')
        this.$store.dispatch('contract/clearAllDiscounts')
    },

    mounted(){
        if((this.$route.params.id == 'nieuw' || !_.has(this.$route.params, 'id')) && _.has(this.$route.query, 'box')){
            try {
                this.findBoxAndSet(this.$route.query.box)
            } catch (error) {
                console.error('[❌] Error while finding and setting box!', error)
            }

            if(_.has(this.$route.query, 'customer')){
                this.$refs['CRUDcustomer'].fetchCustomerWithID(parseInt(this.$route.query.customer))
            }
        }
    },

    methods: {
        startUp(){
            if (!this.user?.organisations) return setTimeout(() => this.startUp(), 1000) // Wait for organisations to be loaded

            Vue.set(this, 'canRemoveCustomer', true)

            Vue.set(this.record.step1, 'invoice_freq', 'month')
            Vue.set(this.record.step1, 'payment_type', this.user.organisation_incasso ? 'incasso' : 'manual')
            Vue.set(this.record.step1, 'organisation', this.user.organisation)

            const currentOrg = this.user.organisations[this.user.organisation]
            const defaultLocationId = currentOrg ? currentOrg.default_location_id : null

            if (defaultLocationId) {
                Vue.set(this.record.step1, 'location', defaultLocationId)
            } else if (this.user.organisation_locations && this.user.organisation_locations.length > 0) {
                Vue.set(this.record.step1, 'location', this.user.organisation_locations[0].id)
            }

            this.fetchStandardContractProducts(this.user.organisation)

            this.record.step2 = {}
            Vue.set(this.record.step2, 'customer_id', null)

            if(_.has(this.$route.query, 'customer')){
                Vue.set(this.record.step2, 'customer_id', parseInt(this.$route.query.customer))
            }

            if (_.has(this.user.organisation_settings, 'preferred_invoice_freq')){
                Vue.set(this.record.step1, 'invoice_freq', this.user.organisation_settings.preferred_invoice_freq)
            }

            this.calculateContractEndDate()
        },

        linkCustomer(customer){
            if(!customer) return

            Vue.set(this.record.step2, 'customer_id', customer.id)
            Vue.set(this.record.step2, 'customer', customer)
        },

        removeCustomer(){
            Vue.set(this.record.step2, 'customer_id', null)

            if(this.record.step2.customer != 'undefined') {
                Vue.delete(this.record.step2, 'customer')
            }

            this.$refs.findCustomer.searchCustomer()
        },

        nextStep(){
            this.validateStep((this.activeStep + 1)).then((validationResponse) => {
                const goingToStep = this.activeStep + 1

                // If we just validated step 1 and this is the first contract for the selected location, show info modal
                if (goingToStep === 1 && validationResponse && validationResponse.first_contract_for_location === true) {
                    this.show_first_location_modal = true
                    this.has_multiple_locations = validationResponse.has_multiple_locations === true
                }

                // Intercept transition from Producten (2) to Overzicht (3) for weekly frequencies
                if (this.activeStep === 2 && typeof this.record.step1.invoice_freq === 'string' && this.record.step1.invoice_freq.indexOf('week_') === 0 && !this.record.step1.price_calc_mode) {
                    this.show_calc_mode_modal = true
                    return
                }

                if(this.activeStep <= 3){
                    Vue.set(this.validatedSteps, 'step'+this.activeStep, true)
                    ++this.activeStep

                    if(this.$refs.CRUDcustomer.mode == 'edit'){
                        this.$refs.CRUDcustomer.cancelEdit()
                    }
                } else {
                    this.fullPageLoader(true)
                }
            }).catch((error) => {
                try {
                    if(_.has(error.data, 'errors')){
                        this.errors[(this.activeStep+1)] = {
                            ...this.errors[(this.activeStep+1)],
                            ...error.data.errors
                        }

                        if(typeof(error.data.errors['customer_id']) != 'undefined'){
                            const customerIdError = error.data.errors['customer_id'][0] || ''
                            const message = customerIdError.includes('verwijderd')
                                ? customerIdError
                                : 'Geen klant geselecteerd!'
                            this.$buefy.toast.open({ message, type: 'is-danger', duration: 4000 })
                        }
                    } else {
                        this.$buefy.toast.open({message: 'Daar gaat iets fout! Probeer het opnieuw a.u.b.', type: 'is-danger'})
                    }

                    if(this.activeStep === 2){
                        this.$buefy.toast.open({message: 'Zonder box of product kan geen contract gemaakt worden!', type: 'is-danger'})
                        let message = 'Daar gaat iets mis! Probeer het opnieuw.';

                        if (error.data?.errors && error.data.errors.length > 0) {
                            if (error.data.errors[0]) {
                                message = error.data.errors[0];
                            }
                        } else if (error.data?.errors && Object.keys(error.data.errors).length > 0) {
                            console.log('Kom je hier?', error.data.errors)

                            if (error.data.errors[Object.keys(error.data.errors)[0]]) {
                                message = error.data.errors[Object.keys(error.data.errors)[0]];
                            }
                        }

                        this.$buefy.toast.open({
                            message: message,
                            type: 'is-danger',
                            duration: 3000
                        })
                    }
                } catch (error) {
                    console.error('[❌] Error displaying error for step: '+this.activeStep)
                }
            }).then(() => {
                this.fullPageLoader(false)
            })
        },

        applyCalcMode(mode){
            try {
                Vue.set(this.record.step1, 'price_calc_mode', mode)
            } catch (e) {}

            this.show_calc_mode_modal = false

            // Proceed to overview now that mode is set
            Vue.set(this.validatedSteps, 'step'+this.activeStep, true)
            ++this.activeStep
        },

        validateStep(step){
            console.log('[🔥] Validating step: '+step)

            this.fullPageLoader(true)
            this.errors[step] = {}

            // Prepare validation data
            const validationData = step === 3 ?
                this.step3Data : // Use Vuex store data for step3
                this.record['step'+step]

            // Add ibanRequired flag to step 2 validation if needed
            if (step === 2) {
                validationData.ibanRequired = this.ibanRequired
            }

            return new Promise((resolve, reject) => {
                axios.post('/api/contract/validate/step'+step, validationData, {
                    headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
                }).then((response) => {
                    resolve(response.data)
                }).catch((error) => {
                    console.error('[❌] Error while validating step: '+step)

                    // Show specific error message for missing IBAN
                    if (step === 2 && error.response?.data?.errors?.banknumber) {
                        const paymentName = this.payment_types[this.record.step1.payment_type].label

                        this.$buefy.toast.open({
                            message: 'IBAN-gegevens zijn verplicht bij de gekozen betaalmethode ('+paymentName.toLowerCase()+')!',
                            type: 'is-danger',
                            duration: 5000
                        })
                    }

                    reject(error.response)
                })
            })
        },

        prevStep(){
            --this.activeStep
        },

        showCreateModal() {
            this.saving = true

            // First validate if a duplicate contract exists
            this.validateContractCreation().then(() => {
                // If no duplicate found, show create modal
                this.show_create_modal = true
                this.saving = false
            }).catch((error) => {
                this.saving = false

                if (error.response) {
                    let dialogConfig = {
                        title: 'Let op!',
                        message: error.response.data.message,
                        confirmText: 'Contract aanmaken',
                        cancelText: 'Annuleren',
                        hasIcon: true,
                        type: error.response.data.type || 'is-warning',
                        onConfirm: () => {
                            this.show_create_modal = true
                        }
                    }

                    const identifier = error.response.data.id || 'unknown'
                    switch (identifier) {
                        case 'duplicate-contract':
                            const contractId = error.response.data.existing_contract.id
                            const contractUrl = error.response.data.contract_url
                            const startDate = moment(error.response.data.existing_contract.start_date).format('DD-MM-YYYY')
                            const customerName = _.has(this.record.step2, 'customer') ?
                                this.record.step2.customer.firstname + ' ' + this.record.step2.customer.lastname : ''
                            dialogConfig.confirmText = 'Doorgaan'

                            dialogConfig.title = 'Dubbel contract waarschuwing'
                            dialogConfig.message = `Weet je zeker dat je het contract wil aanmaken, er is al een contract actief voor ${customerName} met startdatum ${startDate}.<br><br>Bekijk het contract: <a href="${contractUrl}" target="_blank">Contract #${contractId}</a>`
                            dialogConfig.type = 'is-info'
                            break

                        case 'one-time-products':
                            const products = this.step3Data?.products?.map(product => product.id)

                            dialogConfig.confirmText = 'Losse factuur aanmaken'
                            dialogConfig.onConfirm = () => {
                                this.$router.push({name: 'CreateInvoiceRecord', query: { type: 'los', customer: this.record.step2.customer_id, products: products ? products.join(',') : null }})
                            }
                            break

                        case 'box-in-use':
                            dialogConfig.confirmText = 'Doorgaan'
                            dialogConfig.message = `${error.response.data.message}<br>Bekijk het contract: <a href="/panel/contracten/${error.response.data.contract}" target="_blank">Contract #${error.response.data.contract}</a>`
                            break;

                        case 'validation-error':
                        default:
                            // Fallback
                            dialogConfig.message = 'Er is een fout opgetreden bij het valideren van het contract.'
                            dialogConfig.type = 'is-danger'
                            dialogConfig.confirmText = 'Sluiten'
                            dialogConfig.onConfirm = () => {} // Do nothing.

                            break;
                    }

                    // Now launch!
                    this.showValidationDialog({
                        ...dialogConfig,
                    })
                }
            })
        },

        showValidationDialog(config) {
            this.$buefy.dialog.confirm({
                title: config.title,
                message: config.message,
                confirmText: config.confirmText,
                cancelText: config.cancelText,
                type: config.type,
                hasIcon: config.hasIcon,
                onConfirm: config.onConfirm
            })
        },

        validateContractCreation() {
            return new Promise((resolve, reject) => {
                // Prepare validation data
                const validationData = {
                    organisation: this.record.step1.organisation,
                    customer_id: this.record.step2.customer_id,
                    start_date: this.record.step1.start_date,
                    products: this.step3Data
                }

                axios.post('/api/contract/validate-creation', validationData, {
                    headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
                }).then((response) => {
                    resolve(response.data)
                }).catch((error) => {
                    reject(error)
                })
            })
        },

        cancelCreatingContract(){
            this.show_create_modal = false
            this.create_status = ''
        },

        create(createSteps){
            let timeoutTime = 2500
            // console.log('Create contract!', createSteps)
            this.create_status = 'creating_contract'

            let finalizeSteps = qs.stringify(createSteps, { addQueryPrefix: false })

            setTimeout(() => {
                this.save().then((saveResponse) => {
                    let contractId = saveResponse.data.id
                    this.create_status = 'creating_invoices'

                    setTimeout(() => {
                        this.createInvoices(contractId).then((response) => {

                            setTimeout(() => {
                                this.create_status = 'finalizing'

                                this.finalizeContract(contractId, finalizeSteps).then((response) => {
                                    this.create_status = 'created'

                                    setTimeout(() => {
                                        if (createSteps.connect) {
                                            this.$router.push({name: 'ConnectRecord', params: {id: 'nieuw' }, query: {
                                                customer: saveResponse.data.customer,
                                                contract: saveResponse.data.id,
                                                nieuw: true
                                            }})
                                        } else {
                                            this.$router.push({name: 'ContractsRecordView', params: {id: contractId}, query: { nieuw: true }})
                                        }
                                    }, timeoutTime)
                                }).catch((error) => {
                                    console.error('[❌] Error while creating pdfs and mail!', error)
                                    this.$buefy.toast.open({message: 'Er gaat iets fout bij het maken van de PDFs en e-mail!', type: 'is-danger'})
                                    this.cancelCreatingContract()
                                })
                            }, timeoutTime)
                        }).catch((error) => {
                            console.error('[❌] Error while creating invoices!', error)
                            this.$buefy.toast.open({message: 'Er gaat iets fout bij het maken van de facturen!', type: 'is-danger'})
                            this.cancelCreatingContract()
                        })
                    }, timeoutTime)
                }).catch((error) => {
                    console.error('[❌] Error while saving contract!', error)
                    this.$buefy.toast.open({message: 'Contract kan niet aangemaakt worden, er ontbreken gegevens!', type: 'is-danger'})
                    this.cancelCreatingContract()
                })
            }, timeoutTime)
        },

        save(){
            this.saving = true

            // Prepare save data with step3 from Vuex store
            const step3Data = this.step3Data
            const allDiscounts = this.$store.getters['contract/getAllDiscounts']

            // Attach discounts to products in the correct format for backend
            const productsWithDiscounts = (step3Data.products || []).map(product => {
                const discountInfo = allDiscounts[`products_${product.id}`]
                if (discountInfo && discountInfo.discounts && discountInfo.discounts.length > 0) {
                    // Format discounts for backend
                    const formattedDiscounts = discountInfo.discounts.map((discount, index) => {
                        // Ensure description is a string, not an object/array
                        const desc = typeof discount.description === 'string' ? discount.description : null

                        return {
                            type: discount.type,
                            value: parseFloat(discount.value),
                            description: desc,
                            discount_preset: discount.discount_preset || null,
                            invoice: discountInfo.applyToAll ? null : (index + 1),
                            apply_to_all: discountInfo.applyToAll
                        }
                    })

                    console.log('Product discounts being saved:', product.name, formattedDiscounts)
                    return { ...product, discounts: formattedDiscounts }
                }
                return product
            })

            const boxesWithDiscounts = (step3Data.boxes || []).map(box => {
                const discountInfo = allDiscounts[`boxes_${box.id}`]
                if (discountInfo && discountInfo.discounts && discountInfo.discounts.length > 0) {
                    const formattedDiscounts = discountInfo.discounts.map((discount, index) => {
                        // Ensure description is a string, not an object/array
                        const desc = typeof discount.description === 'string' ? discount.description : null

                        return {
                            type: discount.type,
                            value: parseFloat(discount.value),
                            description: desc,
                            discount_preset: discount.discount_preset || null,
                            invoice: discountInfo.applyToAll ? null : (index + 1),
                            apply_to_all: discountInfo.applyToAll
                        }
                    })

                    console.log('Box discounts being saved:', box.number || box.name, formattedDiscounts)
                    return { ...box, discounts: formattedDiscounts }
                }
                return box
            })

            const saveData = {
                ...this.record,
                step3: {
                    products: productsWithDiscounts,
                    boxes: boxesWithDiscounts
                }
            }

            return new Promise((resolve, reject) => {
                axios.post(`/api/contract/nieuw/save`, saveData, {
                    headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
                }).then((response) => {
                    resolve(response)
                }).catch((e) => {
                    reject(e)
                }).then(() => {
                    this.saving = false
                })
            })
        },

        findBoxAndSet(boxId){
            axios.get(`/api/boxes/${boxId}`, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                // Add box directly through Vuex
                this.$store.dispatch('contract/addProduct', {
                    type: 'boxes',
                    product: response.data
                })
            }).catch((error) => {
                console.error('[❌] Error while finding and setting box!', error)
            })
        },

        createInvoices(contractId){
            return axios.get('/api/invoice/first/'+contractId, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            })
        },

        finalizeContract(contractId, params){
            return axios.get('/api/contract/'+contractId+'/finalize'+params, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            })
        },

        calculateContractEndDate(){
            if(this.record.step1.length && this.record.step1.invoice_freq){
                const startDate = moment(this.record.step1.start_date)
                const freq = this.record.step1.invoice_freq
                const numInvoices = this.record.step1.length
                if (typeof freq === 'string' && freq.indexOf('week_') === 0) {
                    // Weekly variants: week_1..week_5
                    const parts = freq.split('_')
                    const weeksPerInvoice = parts.length > 1 ? parseInt(parts[1]) : 1
                    const safeWeeksPerInvoice = isNaN(weeksPerInvoice) ? 1 : weeksPerInvoice
                    // Contract expires at the end of ISO week of the last weekly period
                    this.contractExpirationDate = startDate.clone().endOf('isoWeek').add((safeWeeksPerInvoice * numInvoices) - 1, 'weeks')
                } else {
                    let monthsToAdd = 0

                    // Calculate months to add based on frequency and number of invoices
                    if (freq === 'month') {
                        monthsToAdd = numInvoices
                    } else if (freq === 'quarter') {
                        monthsToAdd = numInvoices * 3
                    } else if (freq === 'halfyear') {
                        monthsToAdd = numInvoices * 6
                    } else if (freq === 'year') {
                        monthsToAdd = numInvoices * 12
                    }

                    if(this.invoiceType == 'pre15'){
                        // For pre15, we start from the end of the current month
                        this.contractExpirationDate = startDate.clone().endOf('month').add(monthsToAdd - 1, 'months')
                    } else {
                        // For after15, we start from the end of next month
                        this.contractExpirationDate = startDate.clone().endOf('month').add(monthsToAdd, 'months')
                    }
                }
            } else {
                this.contractExpirationDate = null
            }

            // Calculate second invoice date
            const startDate = moment(this.record.step1.start_date)
            const freq = this.record.step1.invoice_freq
            let nextDate = startDate.clone()

            if (typeof freq === 'string' && freq.indexOf('week_') === 0) {
                // Weekly variants: next invoice after X weeks
                const parts = freq.split('_')
                const weeksPerInvoice = parts.length > 1 ? parseInt(parts[1]) : 1
                const safeWeeksPerInvoice = isNaN(weeksPerInvoice) ? 1 : weeksPerInvoice
                nextDate = nextDate.add(safeWeeksPerInvoice, 'weeks')
            } else {
                if(this.invoiceType == 'pre15'){
                    // For pre15, next invoice is first of next month
                    nextDate = nextDate.startOf('month').add(1, 'month')

                    // For quarterly/yearly, add additional months
                    if (freq === 'quarter') {
                        nextDate.add(2, 'months') // Add 2 more months (total 3 months from start)
                    } else if (freq === 'halfyear') {
                        nextDate.add(5, 'months') // Add 5 more months (total 6 months from start)
                    } else if (freq === 'year') {
                        nextDate.add(11, 'months') // Add 11 more months (total 12 months from start)
                    }
                } else {
                    // For after15, next invoice is first of month after next
                    nextDate = nextDate.startOf('month').add(2, 'months')

                    // For quarterly/yearly, add additional months
                    if (freq === 'quarter') {
                        nextDate.add(1, 'month') // Add 1 more month (total 3 months)
                    } else if (freq === 'halfyear') {
                        nextDate.add(5, 'months') // Add 5 more months (total 6 months)
                    } else if (freq === 'year') {
                        nextDate.add(10, 'months') // Add 10 more months (total 12 months)
                    }
                }
            }

            this.secondInvoiceDate = nextDate.format('YYYY-MM-DD')
        },

        fullPageLoader(value){
            this.saving = value
        },

        customerModeChanged(value){
            // console.log('customerModeChanged', value)

            if(value == 'edit'){
                this.showFormFooter = false
            } else if(value == 'view'){
                if(this.mode == 'new'){
                    this.showFormFooter = true
                }
            }
        },

        fetchStandardContractProducts(orgid) {
            this.$store.dispatch('contract/fetchBaseProducts').catch(error => {
                console.error('Kan geen base products ophalen!')
            })
        },

        setNextStepBlockedByInvalidBox(bool) {
            this.nextStepBlockedByInvalidBox = bool
        }
    },

    watch: {
        // Update invoice numbers
        activeStep: function(newVal){
            if(newVal === 3){
                setTimeout(() => {
                    Vue.set(this, 'overviewKey', ++this.overviewKey)
                }, 500)
            }
        },

        // Step 1
        'record.step1.start_date': function(newVal){
            this.calculateContractEndDate()
            this.$store.dispatch('contract/setStartDate', newVal)
        },
        'record.step1.length': function(newVal){
            this.calculateContractEndDate()
        },
        'record.step1.invoice_freq': function(newVal){
            this.calculateContractEndDate()
            this.$store.dispatch('contract/setInvoiceFreq', newVal)

            // Reset calculation mode when leaving weekly frequencies
            if (!(typeof newVal === 'string' && newVal.indexOf('week_') === 0)) {
                Vue.set(this.record.step1, 'price_calc_mode', null)
            }
        },

        'record.step1.organisation': function(newVal){
            // Delete all products when switching organisations as PIT admin.
            if (this.user.role === 'pit') {
                this.$store.dispatch('contract/clearProducts', 'products')
            }

            if(this.$route.params.id == 'nieuw') {
                this.fetchStandardContractProducts(newVal)
            }
        },

        undetermined: function(newVal, oldVal){
            if(this.mode != 'saving'){
                Vue.set(this.record.step1, 'length', newVal ? 0 : 2)
            }
        },

        // Step 2
        'record.step2.customer_id': function(newVal){
            if(newVal && typeof newVal == 'number'){
                Vue.set(this.validatedSteps, 'step1', true)
            } else {
                Vue.set(this.validatedSteps, 'step1', false)
            }
        },

        // Step 3
        step3Data: {
            handler(newVal) {
                const hasProducts = newVal.products.length > 0
                const hasBoxes = newVal.boxes.length > 0
                Vue.set(this.validatedSteps, 'step2', hasProducts || hasBoxes)
            },
            deep: true
        }
    }
}
</script>
